import React from 'react'
import clsx from 'clsx'
import Select from './Select'
import { useFormField } from '@hooks/useFormField'

type LabelledSelectProps<T = any> = {
  name: string
  label: string
  placeholder?: string
  errors: any
  onChange: (value: T) => void
  value: T
  options: Array<{ value: T; label: string }>
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void
  disabled?: boolean
  description?: string
  required?: boolean
}

function LabelledSelect({ name, label, description, errors, onChange, value, required, ...props }: LabelledSelectProps, ref: React.ForwardedRef<any>) {
  const { fieldError, errorId, descriptionId, requiredText } = useFormField(
    name,
    errors,
    required,
    description
  )

  function handleChange(value: any) {
    onChange(value)
  }

  return (
    <div className="tw-flex tw-flex-col tw-justify-center tw-min-w-[0]" data-testid="custom-select">
      <label id={`${name}.label`} className={clsx(
        'tw-mb-0 tw-pb-1 tw-text-gray-700 tw-font-medium',
        fieldError && 'tw-text-red-500'
      )}>
        {label} {requiredText}
      </label>
      <Select 
        name={name} 
        errors={errors} 
        value={value} 
        onChange={handleChange} 
        ref={ref} 
        {...props} 
      />
      
      {/* Note: react-select has a known accessibility limitation where it overrides 
          custom aria-describedby attributes with its own internal ones (e.g., react-select-X-placeholder).
          This prevents proper linking of error messages and descriptions to the select input.
          See: https://github.com/JedWatson/react-select/issues/5651
          TODO: Consider replacing with a more accessible select library */}

      {description && (
        <p id={descriptionId} className={clsx(
          'tw-text-sm tw-text-gray-600 tw-pt-1',
          fieldError && 'tw-text-red-500'
        )}>
          {description}
        </p>
      )}

      <div role="status" aria-relevant="all" data-testid={`${name}.error`}>
        {fieldError && (
          <p id={errorId} className="tw-text-red-500 tw-font-light tw-text-sm tw-pt-1">
            {fieldError.message}
          </p>
        )}
      </div>
    </div>
  )
}

export default React.forwardRef(LabelledSelect)
